import React, { useEffect } from 'react';
import * as qs from 'query-string';
import styled from 'styled-components';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import LockIcon from '@mui/icons-material/Lock';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PageHeaderList } from '../../../../common/pageheaderlist/pageheaderlist';
import { ListWrapper } from '../../../../common/layout/wrapper';
import { Breadcrumbs } from '../../../../common/breadcrumbs/breadcrumbs';
import { FlagGroupError } from '../../../../common/error/error';
import { ButtonCustom } from '../../../../common/button/button';
import { Text } from '../../../../common/text/Text';
import { Lozenge } from '../../../../common/lozenge/Lozenge';
import { Loader } from '../../../../common/loader/loader';
import { CapGauge } from '../components/capgauge';
import { createHeadTableSaisine, createRowsTableSaisine } from './tableutils';
import { AddClaimSearch } from './components/addClaimSearch';
import { saisineErrorLabel } from '../../../usecases/saisine.reducer';

const SaisineContainer = ({
  search,
  saisine,
  loading,
  actionLoading,
  errors,
  eligibleClaims = [],
  eligibleLoading,
  retrieveSaisine,
  addClaim,
  removeClaim,
  deleteSaisine,
  validateSaisine,
  generateDocs,
  searchEligibleClaims,
  clearEligibleClaims,
  dismissFlag,
  clearSaisineState,
}) => {
  const { id } = qs.parse(search);

  useEffect(() => {
    if (id) retrieveSaisine(id);
    return () => clearSaisineState();
  }, [id]);

  if (!saisine) {
    return (
      <ListWrapper>
        <FlagGroupError data={errors} dismissFlag={dismissFlag} />
        {loading && <Loader />}
      </ListWrapper>
    );
  }

  const isOpen = saisine.status !== 'SENT';
  const claimIds = saisine.claims.map(({ claim }) => claim._id);
  const firstDate = saisine.firstFlightDate();

  const onRemove = (claimId) => {
    if (window.confirm('Retirer ce dossier de la saisine ?')) {
      removeClaim(saisine._id, claimId);
      clearEligibleClaims();
    }
  };

  const onDelete = () => {
    if (window.confirm('Supprimer cette saisine ? Les dossiers seront libérés, leurs statuts ne changent pas.')) {
      deleteSaisine(saisine._id);
    }
  };

  const onValidate = () => {
    if (window.confirm(`Envoyer la saisine ${saisine.label()} ? Les ${saisine.nbClaims()} dossiers passeront en « Saisine envoyée ».`)) {
      validateSaisine(saisine._id);
    }
  };

  return (
    <ListWrapper>
      <FlagGroupError data={errors} dismissFlag={dismissFlag} />
      <PageHeaderList
        breadcrumbs={(
          <Breadcrumbs
            fullName={saisine.label()}
            key="SaisineKey"
            location="/saisines?paginateSize=50"
            text="Saisines"
          />
        )}
        actionsContent={(
          <>
            <ButtonCustom
              text="Générer les documents"
              onClick={() => generateDocs(claimIds)}
              disabled={actionLoading || !claimIds.length || (isOpen && !saisine.generationReady())}
              startIcon={<DescriptionIcon />}
            />
            <ButtonCustom
              text="Envoyer la saisine"
              onClick={onValidate}
              disabled={!isOpen || actionLoading || !claimIds.length || saisine.warnings.length > 0}
              startIcon={<SendIcon />}
            />
            <ButtonCustom
              text="Supprimer"
              onClick={onDelete}
              disabled={!isOpen || actionLoading}
              startIcon={<DeleteOutlineIcon />}
            />
          </>
        )}
      />
      <SummaryContainer>
        <SummaryLine>
          <LockIcon fontSize="small" style={{ color: '#6b778c' }} />
          <Text color="secondary" variant="body2">
            {`Juridiction et compagnie verrouillées : ${saisine.jurisdiction} · ${saisine.airline_name}`}
          </Text>
          {isOpen ? <Lozenge text="Ouverte" green /> : <Lozenge text="Envoyée" blue />}
        </SummaryLine>
        <SummaryLine>
          <Text color="secondary" variant="body2">
            {firstDate
              ? `${saisine.flightRange()} — fenêtre max 30 jours · ${saisine.nbClaims()} dossiers · ${saisine.nbPassengers()} pax`
              : 'Aucun dossier dans la saisine'}
          </Text>
        </SummaryLine>
        {isOpen && saisine.nbClaims() > 0 && (
          <SummaryLine>
            {saisine.generationReady()
              ? <Lozenge text="Saisine générable" green />
              : <Lozenge text={`Saisine non générable — ${saisine.nbClaimsNotReady()} dossier(s) avec pax incomplets`} red />}
          </SummaryLine>
        )}
        <GaugeLine>
          <CapGauge total={saisine.computedTotal} warning={saisine.warnings.length > 0} large />
          {isOpen && (
            <Text color="secondary" variant="body2">
              {`Reste ${saisine.remaining().toLocaleString('fr-FR')} €`}
            </Text>
          )}
        </GaugeLine>
      </SummaryContainer>
      {saisine.warnings.length > 0 && (
        <WarningBanner>
          <Text color="secondary" variant="body2" component="div">
            {`L'envoi est bloqué : ${saisine.warnings.map((warning) => saisineErrorLabel(warning)).join(' · ')}. Retirer ou corriger les dossiers concernés.`}
          </Text>
        </WarningBanner>
      )}
      <DynamicTableStateless
        head={createHeadTableSaisine(isOpen)}
        rows={createRowsTableSaisine(saisine, onRemove)}
        loadingSpinnerSize="large"
        isLoading={loading}
        isFixedSize
      />
      {isOpen && (
        <AddClaimSearch
          saisine={saisine}
          eligibleClaims={eligibleClaims}
          loading={eligibleLoading}
          search={searchEligibleClaims}
          add={(saisineId, claimId) => {
            addClaim(saisineId, claimId);
            clearEligibleClaims();
          }}
        />
      )}
    </ListWrapper>
  );
};

const SummaryContainer = styled.div`
  border: 1px solid #dfe1e6;
  border-radius: 4px;
  padding: 12px 15px;
  margin: 10px 0;
`;

const SummaryLine = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const GaugeLine = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WarningBanner = styled.div`
  background-color: #fffae6;
  border: 1px solid #ffe380;
  border-radius: 4px;
  padding: 10px 15px;
  margin-bottom: 10px;
`;

export {
  SaisineContainer,
};
